// Desarrollo Urbano · Territorio — datos de la demostración.
// Son capas PRECALCULADAS con datos públicos (Sentinel-2 de Copernicus/ESA, Meta/WRI
// Canopy Height, ESA WorldCover, OpenStreetMap) que viajan como assets estáticos en
// /public/gov-territory. No hay datos personales ni de la org: es imagen satelital
// pública. Cuando exista el endpoint del backend (tool `territorio.*`), este archivo
// es el único punto que cambia.

import type { Geometry } from 'geojson';

export type Categoria = 'probable_obra_o_desmonte' | 'revisar' | 'probable_rotacion_agricola';

export interface HotspotProps {
  rank: number;
  area_ha: number;
  centroide_lat: number;
  centroide_lon: number;
  ndvi_antes: number;
  ndvi_despues: number;
  dndbi: number;
  frac_ndbi_sube: number;
  categoria: Categoria;
  municipio: string;
  worldcover2021: Record<string, number>;
  explicacion: string;
  img?: boolean;
}

export interface ColoniaProps {
  colonia: string;
  area_ha: number;
  dosel_arboreo_pct: number;
  arbustivo_pct: number;
  arboles_estimados: number;
  arboles_por_ha: number;
}

export interface Feature<P> {
  type: 'Feature';
  geometry: Geometry;
  properties: P;
}

export interface Collection<P> {
  type: 'FeatureCollection';
  features: Feature<P>[];
}

export interface VentanaConteo {
  fuente: string;
  ventana: string;
  titulo: string;
  area_ha: number;
  arboles: number;
  arboles_por_ha: number;
  diametro_copa_mediano_m: number | null;
  t_inferencia_cpu_s: number;
  img: string;
}

export interface Resumen {
  generado: string;
  validacion: string;
  vegetacion: {
    fuente: string;
    fechas_antes: string[];
    fechas_despues: string[];
    bbox: [number, number, number, number];
    umbrales: { ndvi_antes_min: number; dndvi_max: number; ndvi_despues_max: number; area_min_ha: number };
    n_hotspots: number;
    area_total_ha: number;
    por_categoria: Record<Categoria, { n: number; ha: number }>;
    por_municipio_obra: Record<string, number>;
  };
  dosel: {
    fuente: string;
    fechas_imagen: string[];
    aoi: { area_ha: number; dosel_arboreo_pct: number; arboles_estimados: number };
    n_colonias: number;
    umbral_arbol_m: number;
  };
  conteo: { modelo: string; resolucion_m: number; advertencia: string; ventanas: VentanaConteo[] };
}

const BASE = `${import.meta.env.BASE_URL}gov-territory/`;

export const assetUrl = (path: string) => `${BASE}${path}`;
export const hotspotImg = (rank: number, cuando: 'antes' | 'despues') =>
  assetUrl(`img/h${String(rank).padStart(3, '0')}_${cuando}.jpg`);

const cache = new Map<string, Promise<unknown>>();
function getJson<T>(file: string): Promise<T> {
  if (!cache.has(file)) {
    const p = fetch(assetUrl(file)).then((r) => {
      if (!r.ok) throw new Error(`No se pudo cargar ${file} (HTTP ${r.status})`);
      return r.json();
    });
    p.catch(() => cache.delete(file));
    cache.set(file, p);
  }
  return cache.get(file) as Promise<T>;
}

export const loadResumen = () => getJson<Resumen>('summary.json');
export const loadHotspots = () => getJson<Collection<HotspotProps>>('hotspots.geojson');
export interface Overlays { [anio: string]: { img: string; bounds: [[number, number], [number, number]] } }
export const loadOverlays = () => getJson<Overlays>('overlays.json');
export const loadColonias = () => getJson<Collection<ColoniaProps>>('dosel_colonias.geojson');

// ── Presentación ───────────────────────────────────────────────────────────
export const CATEGORIAS: Record<Categoria, { label: string; corto: string; color: string; chip: string; ayuda: string }> = {
  probable_obra_o_desmonte: {
    label: 'Probable obra o desmonte',
    corto: 'Obra / desmonte',
    color: '#f43f5e',
    chip: 'border-rose-500/30 bg-rose-500/15 text-rose-700 dark:text-rose-300',
    ayuda: 'Se quitó vegetación y el índice de superficie construida o desnuda (NDBI) subió.',
  },
  revisar: {
    label: 'Revisar',
    corto: 'Revisar',
    color: '#f59e0b',
    chip: 'border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300',
    ayuda: 'Se perdió vegetación sin señal clara de obra: sequía, incendio o desmonte aún sin construir.',
  },
  probable_rotacion_agricola: {
    label: 'Probable rotación agrícola',
    corto: 'Agrícola',
    color: '#94a3b8',
    chip: 'border-border bg-muted text-muted-foreground',
    ayuda: 'En 2021 era cultivo (ESA WorldCover): lo más probable es barbecho o cambio de siembra.',
  },
};

export const ORDEN_CATEGORIAS: Categoria[] = ['probable_obra_o_desmonte', 'revisar', 'probable_rotacion_agricola'];

export const COBERTURA_LABEL: Record<string, string> = {
  arboles: 'Árboles', matorral: 'Matorral', pastizal: 'Pastizal', cultivo: 'Cultivo',
  construido: 'Construido', suelo_desnudo: 'Suelo desnudo', agua: 'Agua', humedal: 'Humedal',
};

export const num = (v: number, d = 1) => v.toLocaleString('es-MX', { minimumFractionDigits: d, maximumFractionDigits: d });
export const entero = (v: number) => Math.round(v).toLocaleString('es-MX');

export function fechaCorta(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es-MX', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });
}

export function mesAnio(iso: string): string {
  const [y, m] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 15)).toLocaleDateString('es-MX', { timeZone: 'UTC', month: 'short', year: 'numeric' });
}

/** Rampa verde para % de dosel (0 → 40 %+). */
export function colorDosel(pct: number): string {
  const stops = ['#f7fcb9', '#d9f0a3', '#addd8e', '#78c679', '#41ab5d', '#238443', '#005a32'];
  const t = Math.max(0, Math.min(1, pct / 40));
  return stops[Math.min(stops.length - 1, Math.floor(t * (stops.length - 1) + 0.0001))];
}
export const DOSEL_LEYENDA = [0, 7, 14, 20, 27, 34, 40];
